import { apiSlice } from '../../../apis/api';
import { getTags } from '../../../apis/apiLib';
import { signalrClient } from '../../../signalr/client';
import {
  SignalCustomExportRunItemStatusChanged,
  SignalCustomExportRunStatusChanged,
} from '../../../signalr/signalrInterfaces';
import { CalculationsCollection } from '../../../swagger/collections';
import {
  Condition,
  CustomExportRunItemResponse,
  CustomExportRunItemResponseCollectionResponse,
  CustomExportRunPatchRequest,
  CustomExportRunResponse,
  CustomExportRunResponseCollectionResponse,
  CustomExportRunStatus,
  CustomExportRunType,
  Operation,
  Sorting,
} from '@ydistri/api-sdk';
import { runVoid } from '../Editation/apiCalculationEdit';

const { TAGS, TAGS_ARRAY } = getTags('calculationExport');
const FAILED_RUN_STATUS_REFRESH_DELAY_MS = 2000;

export interface CustomExportListQuery {
  skip?: number;
  top?: number;
  inlineCount?: boolean;
  search?: string;
  sortings?: Sorting[];
  conditions?: Condition[];
}

export interface GetCustomExportRunsRequest {
  calculationId: number;
  query?: CustomExportListQuery;
}

export interface GetCustomExportRunItemsRequest {
  calculationId: number;
  customExportRunId: number;
  query?: CustomExportListQuery;
}

export interface GetCustomExportRunItemDetailRequest {
  calculationId: number;
  customExportRunId: number;
  customExportRunItemId: number;
}

export interface CustomExportRunMutationRequest {
  calculationId: number;
  type: CustomExportRunType;
}

export interface UpdateCustomExportRunRequest {
  calculationId: number;
  customExportRunId: number;
  data: CustomExportRunPatchRequest;
}

const calculationScopedTagId = (calculationId: number) => `CALC-${calculationId}`;
const runScopedTagId = (calculationId: number, customExportRunId: number) =>
  `RUN-${calculationId}-${customExportRunId}`;
const itemScopedTagId = (
  calculationId: number,
  customExportRunId: number,
  customExportRunItemId: number,
) => `ITEM-${calculationId}-${customExportRunId}-${customExportRunItemId}`;

const getCustomExportMutationInvalidationTags = (calculationId: number) => [
  {
    type: TAGS.latestCustomExportRun,
    id: calculationScopedTagId(calculationId),
  },
  {
    type: TAGS.customExportRuns,
    id: calculationScopedTagId(calculationId),
  },
  {
    type: TAGS.customExportRunItems,
    id: calculationScopedTagId(calculationId),
  },
  {
    type: TAGS.customExportRunItemDetail,
    id: calculationScopedTagId(calculationId),
  },
];

const failedRunStatuses = new Set<CustomExportRunStatus>([
  CustomExportRunStatus.Failed,
  CustomExportRunStatus.FailedPreparing,
  CustomExportRunStatus.DoneWithFailed,
]);

const getCustomExportRunsInvalidationTag = (calculationId: number) => ({
  type: TAGS.customExportRuns,
  id: calculationScopedTagId(calculationId),
});

const getCustomExportRun = async (
  calculationId: number,
  customExportRunId: number,
): Promise<CustomExportRunResponse | undefined> => {
  const { data } = await CalculationsCollection.getCustomExportRuns(calculationId, {
    top: 1,
    conditions: [{ fieldName: 'Id', operation: Operation.Eq, value: customExportRunId }],
  });

  return data.data[0];
};

export const apiCalculationExport = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getCustomExportRuns: builder.query<
        CustomExportRunResponseCollectionResponse,
        GetCustomExportRunsRequest
      >({
        queryFn: async ({ calculationId, query }) => {
          const { data } = await CalculationsCollection.getCustomExportRuns(calculationId, query);
          return { data };
        },
        providesTags: (result, error, arg) => [
          getCustomExportRunsInvalidationTag(arg.calculationId),
        ],
        async onCacheEntryAdded({ calculationId }, api) {
          try {
            await api.cacheDataLoaded;
            const delayedRunRefreshTimeouts = new Set<ReturnType<typeof setTimeout>>();
            let isCacheEntryActive = true;

            const invalidateCustomExportRuns = () => {
              api.dispatch(
                apiCalculationExport.util.invalidateTags([
                  getCustomExportRunsInvalidationTag(calculationId),
                ]),
              );
            };

            const refreshRunErrorMessage = (customExportRunId: number) => {
              getCustomExportRun(calculationId, customExportRunId)
                .then(run => {
                  if (!run || !isCacheEntryActive) {
                    return;
                  }

                  api.updateCachedData(draft => {
                    const cachedRun = draft.data.find(item => item.id === customExportRunId);
                    if (cachedRun) {
                      cachedRun.errorMessage = run.errorMessage;
                    }
                  });
                })
                .catch(() => undefined);
            };

            const scheduleFailedRunRefresh = (
              customExportRunId: number,
              status: CustomExportRunStatus,
            ) => {
              if (!failedRunStatuses.has(status)) {
                return;
              }

              const timeout = setTimeout(() => {
                delayedRunRefreshTimeouts.delete(timeout);
                refreshRunErrorMessage(customExportRunId);
              }, FAILED_RUN_STATUS_REFRESH_DELAY_MS);

              delayedRunRefreshTimeouts.add(timeout);
            };

            const clearDelayedRunRefreshTimeouts = () => {
              for (const timeout of delayedRunRefreshTimeouts) {
                clearTimeout(timeout);
              }
              delayedRunRefreshTimeouts.clear();
            };

            const listener = (signal: SignalCustomExportRunStatusChanged) => {
              if (signal.calculationId !== calculationId) return;

              let wasUpdated = false;

              api.updateCachedData(draft => {
                const run = draft.data.find(item => item.id === signal.customExportRunId);
                if (run) {
                  run.status = signal.status;
                  wasUpdated = true;
                }
              });

              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- updated synchronously inside updateCachedData callback
              if (!wasUpdated) {
                invalidateCustomExportRuns();
              }

              scheduleFailedRunRefresh(signal.customExportRunId, signal.status);
            };

            signalrClient.on('CustomExportRunStatusChanged', listener);

            await api.cacheEntryRemoved;
            isCacheEntryActive = false;
            signalrClient.off('CustomExportRunStatusChanged', listener);
            clearDelayedRunRefreshTimeouts();
          } catch {
            // cache entry was removed before the data finished loading
          }
        },
      }),
      createCustomExportRun: builder.mutation<
        CustomExportRunResponse,
        CustomExportRunMutationRequest
      >({
        queryFn: async ({ calculationId, type }) => {
          const { data } = await CalculationsCollection.createCustomExportRun(calculationId, {
            type,
          });
          return { data: data.data };
        },
        invalidatesTags: (result, error, arg) =>
          getCustomExportMutationInvalidationTags(arg.calculationId),
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void
      updateCustomExportRun: builder.mutation<void, UpdateCustomExportRunRequest>({
        queryFn: async ({ calculationId, customExportRunId, data: requestData }) =>
          runVoid(() =>
            CalculationsCollection.updateCustomExportRun(
              calculationId,
              customExportRunId,
              requestData,
            ),
          ),
        invalidatesTags: (result, error, arg) =>
          getCustomExportMutationInvalidationTags(arg.calculationId),
      }),
      getCustomExportRunItems: builder.query<
        CustomExportRunItemResponseCollectionResponse,
        GetCustomExportRunItemsRequest
      >({
        queryFn: async ({ calculationId, customExportRunId, query }) => {
          const { data } = await CalculationsCollection.getCustomExportRunItems(
            calculationId,
            customExportRunId,
            query,
          );
          return { data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.customExportRunItems,
            id: calculationScopedTagId(arg.calculationId),
          },
          {
            type: TAGS.customExportRunItems,
            id: runScopedTagId(arg.calculationId, arg.customExportRunId),
          },
        ],
        async onCacheEntryAdded({ calculationId, customExportRunId }, api) {
          try {
            await api.cacheDataLoaded;

            const listener = (signal: SignalCustomExportRunItemStatusChanged) => {
              if (
                signal.calculationId !== calculationId ||
                signal.customExportRunId !== customExportRunId
              ) {
                return;
              }

              let wasUpdated = false;

              api.updateCachedData(draft => {
                const item = draft.data.find(row => row.id === signal.customExportRunItemId);
                if (item) {
                  item.status = signal.status;
                  wasUpdated = true;
                }
              });

              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- updated synchronously inside updateCachedData callback
              if (!wasUpdated) {
                api.dispatch(
                  apiCalculationExport.util.invalidateTags([
                    {
                      type: TAGS.customExportRunItems,
                      id: calculationScopedTagId(calculationId),
                    },
                  ]),
                );
              }
            };

            signalrClient.on('CustomExportRunItemstatusChanged', listener);

            await api.cacheEntryRemoved;
            signalrClient.off('CustomExportRunItemstatusChanged', listener);
          } catch {
            // cache entry was removed before the data finished loading
          }
        },
      }),
      getCustomExportRunItemDetail: builder.query<
        CustomExportRunItemResponse,
        GetCustomExportRunItemDetailRequest
      >({
        queryFn: async ({ calculationId, customExportRunId, customExportRunItemId }) => {
          const { data } = await CalculationsCollection.getCustomExportRunItemDetail(
            calculationId,
            customExportRunId,
            customExportRunItemId,
          );
          return { data: data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.customExportRunItemDetail,
            id: calculationScopedTagId(arg.calculationId),
          },
          {
            type: TAGS.customExportRunItemDetail,
            id: itemScopedTagId(
              arg.calculationId,
              arg.customExportRunId,
              arg.customExportRunItemId,
            ),
          },
        ],
        async onCacheEntryAdded({ calculationId, customExportRunId }, api) {
          try {
            await api.cacheDataLoaded;

            const listener = (signal: SignalCustomExportRunItemStatusChanged) => {
              if (
                signal.calculationId !== calculationId ||
                signal.customExportRunId !== customExportRunId
              ) {
                return;
              }

              let wasUpdated = false;

              api.updateCachedData(draft => {
                if (draft.id === signal.customExportRunItemId) {
                  draft.status = signal.status;
                  wasUpdated = true;
                }
              });

              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- updated synchronously inside updateCachedData callback
              if (!wasUpdated) {
                api.dispatch(
                  apiCalculationExport.util.invalidateTags([
                    {
                      type: TAGS.customExportRunItemDetail,
                      id: calculationScopedTagId(calculationId),
                    },
                  ]),
                );
              }
            };

            signalrClient.on('CustomExportRunItemstatusChanged', listener);

            await api.cacheEntryRemoved;
            signalrClient.off('CustomExportRunItemstatusChanged', listener);
          } catch {
            // cache entry was removed before the data finished loading
          }
        },
      }),
    }),
  });

export const {
  useGetCustomExportRunsQuery,
  useCreateCustomExportRunMutation,
  useUpdateCustomExportRunMutation,
  useGetCustomExportRunItemsQuery,
  useGetCustomExportRunItemDetailQuery,
} = apiCalculationExport;
